/**
 * SignInPinModal — Small sign-in modal triggered from the top bar.
 *
 * Returns null when not open. Two ways in:
 *   - PIN: validates against the instance's reportPin via
 *     ReportAuthContext.signIn() (checked by the backend). Wrong PIN → inline
 *     error; correct PIN → modal closes and the gated content reveals.
 *   - Email (#1475): the email-code sign-in (useOtpLogin). A named report
 *     reader holds a grant on the instance; on success the page reloads so
 *     the report's data and lock state come from the new session. A sign-in
 *     without access to this report says so, and the PIN still works.
 *
 * Mounts via React Portal to document.body. MainViewHeader uses
 * `backdrop-filter: blur(...)` which creates a stacking context — any
 * descendant z-index, however large, can only float within that context
 * (not above sibling stacking contexts). Rendering the modal at the
 * document root escapes that prison so the backdrop covers the whole
 * viewport, including the header.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { X } from 'lucide-react';
import { useReportAuth } from './ReportAuthContext';
import { useReportEdits } from './ReportEditsContext';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import useOtpLogin from '../../hooks/useOtpLogin';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  /* Highest priority — beats anything else in the app. The portal mount
   * already gets us out of header/drawer stacking contexts; the big
   * z-index is belt + braces against any other future overlay. */
  z-index: 2147483000;
  display: flex;
  /* Float ~60px from the top of the viewport on tall screens so the
   * modal sits right under the header without overlapping the page
   * title area. On short screens (mobile landscape, embedded browsers)
   * 60px would push the modal below the visible area, so we center
   * instead. The overflow-y: auto cap means a tall card still scrolls
   * within the backdrop rather than clipping. */
  align-items: flex-start;
  justify-content: center;
  padding: 60px 16px 16px;
  overflow-y: auto;

  @media (max-height: 560px) {
    align-items: center;
    padding: 16px;
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 360px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
  padding: 24px 24px 20px;
  position: relative;
  /* Cap to viewport minus backdrop padding; the form itself never gets
   * this tall but the cap protects against accidental crop on tiny
   * viewports (mobile landscape with the URL bar visible). */
  max-height: calc(100vh - 32px);
  overflow-y: auto;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 12px; right: 12px;
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  &:hover { color: #111; }
`;

const Title = styled.h3`
  margin: 0 0 6px;
  font-size: 1.1rem;
  color: #111;
`;

const Hint = styled.p`
  margin: 0 0 16px;
  font-size: 0.88rem;
  color: #6b7280;
  line-height: 1.4;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  font-size: 1rem;
  border: 1px solid ${p => p.$error ? '#ef4444' : '#d1d5db'};
  border-radius: 8px;
  box-sizing: border-box;
  &:focus { outline: 2px solid #367ABA; outline-offset: 0; border-color: #367ABA; }
`;

const Error = styled.div`
  font-size: 0.85rem;
  color: #ef4444;
  margin-top: 8px;
  min-height: 1.2em;
`;

const Submit = styled.button`
  margin-top: 16px;
  width: 100%;
  padding: 10px 16px;
  background: #111;
  color: #fff;
  font-weight: 600;
  font-size: 0.95rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  &:hover { background: #000; }
`;

const Note = styled.p`
  margin: 0 0 12px;
  font-size: 0.85rem;
  color: ${p => p.$tone === 'ok' ? '#15803d' : '#6b7280'};
  line-height: 1.4;
`;

const LinkBtn = styled.button`
  display: block;
  margin: 14px auto 0;
  min-height: 44px;
  background: none;
  border: none;
  color: #367ABA;
  font-size: 0.9rem;
  text-decoration: underline;
  cursor: pointer;
  &:disabled { color: #9ca3af; cursor: default; }
`;

const InlineLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: #367ABA;
  font: inherit;
  text-decoration: underline;
  cursor: pointer;
`;

function EmailSignIn({ inputRef }) {
  const { t } = useTranslation();
  const otp = useOtpLogin({
    onSuccess: () => window.location.reload(),
    resendNotice: t('pinLogin.codeResent'),
    successNotice: t('pinLogin.signingIn'),
  });
  useEffect(() => { inputRef.current?.focus(); }, [otp.step, inputRef]);

  if (otp.step !== 'code') {
    return (
      <form onSubmit={(e) => { e.preventDefault(); otp.requestCode(); }}>
        <Hint>{t('report.signIn.emailHint')}</Hint>
        <Input
          ref={inputRef}
          type="email"
          autoComplete="email"
          value={otp.email}
          onChange={(e) => otp.setEmail(e.target.value)}
          placeholder={t('pinLogin.emailPlaceholder')}
          disabled={otp.busy}
          $error={!!otp.error}
          aria-invalid={!!otp.error}
        />
        <Error>{otp.error}</Error>
        <Submit type="submit" disabled={otp.busy || !otp.email.includes('@')}>
          {otp.busy ? t('pinLogin.sending') : t('pinLogin.sendCode')}
        </Submit>
      </form>
    );
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); otp.verifyCode(); }}>
      <Hint>
        {t('pinLogin.codeSentTo')} <strong>{otp.email}</strong>{' '}
        (<InlineLink type="button" onClick={otp.changeEmail}>{t('pinLogin.changeEmail')}</InlineLink>)
      </Hint>
      <Note>{t('pinLogin.codeIfInvited')}</Note>
      <Input
        ref={inputRef}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={otp.code}
        onChange={(e) => otp.setCode(e.target.value)}
        placeholder="••••••"
        disabled={otp.busy}
        $error={!!otp.error}
        aria-invalid={!!otp.error}
      />
      <Error>{otp.error}</Error>
      {otp.notice && <Note $tone="ok">{otp.notice}</Note>}
      <LinkBtn type="button" onClick={() => otp.requestCode(true)} disabled={otp.busy || otp.cooldown > 0}>
        {otp.cooldown > 0 ? t('pinLogin.resendIn', { s: otp.cooldown }) : t('pinLogin.resend')}
      </LinkBtn>
    </form>
  );
}

export default function SignInPinModal({ open, onClose }) {
  const { signIn, accountAuthed } = useReportAuth();
  const { me } = useReportEdits();
  const { t } = useTranslation();
  const [mode, setMode] = useState('pin');
  const [pin, setPin] = useState('');
  const [err, setErr] = useState('');
  const inputRef = useRef(null);

  // Autofocus the input + clear stale state every time the modal opens.
  useEffect(() => {
    if (!open) return;
    setMode('pin');
    setPin('');
    setErr('');
    setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  // ESC closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (await signIn(pin)) onClose?.();
    else setErr(t('report.signIn.error'));
  };

  // Portal to document.body — escapes any ancestor stacking context
  // (notably MainViewHeader's backdrop-filter). SSR-safe via the
  // typeof check; in a browser document is always available here.
  if (typeof document === 'undefined') return null;
  return createPortal(
    <Backdrop onClick={onClose}>
      <Card onClick={(e) => e.stopPropagation()}>
        <CloseBtn onClick={onClose} aria-label={t('report.signIn.closeAriaLabel')}><X size={18} /></CloseBtn>
        <Title>{t('report.signIn.title')}</Title>
        {me?.email && !accountAuthed && (
          <Note>{t('report.signIn.noAccess', { email: me.email })}</Note>
        )}
        {mode === 'email' ? <EmailSignIn inputRef={inputRef} /> : (<>
        <Hint>{t('report.signIn.hint')}</Hint>
        <form onSubmit={handleSubmit}>
          <Input
            ref={inputRef}
            type="text"
            value={pin}
            onChange={(e) => { setPin(e.target.value); if (err) setErr(''); }}
            placeholder={t('report.signIn.placeholder')}
            autoComplete="off"
            $error={!!err}
            aria-invalid={!!err}
          />
          <Error>{err}</Error>
          <Submit type="submit">{t('report.signIn.submit')}</Submit>
        </form>
        </>)}
        <LinkBtn type="button" onClick={() => setMode(mode === 'pin' ? 'email' : 'pin')}>
          {mode === 'pin' ? t('report.signIn.useEmail') : t('report.signIn.usePin')}
        </LinkBtn>
      </Card>
    </Backdrop>,
    document.body,
  );
}
